import { useLocalization } from '@ais-ui/locale-management';
import {
  Button,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  InfoIconTooltip,
  Input,
  useToast,
} from '@ais-ui/ui-core';
import { useSaveSettings } from '@ais-ui/websockets';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import * as z from 'zod';
import { useSettings } from '../SettingsProvider';
import { useSettingsPage } from '../SettingsPageProvider';

// In the backend (olserver) it is called lost and deleted. However, we would
// like to call it lost and removed for the customer to better understand.
interface SourceTypeTimeouts {
  deleted: number;
  dst: number;
  lost: number;
}

enum SourceTypes {
  ais,
  radar,
  lrit,
  aisSat,
  adsB,
  pps,
  land,
  aisVessel,
}

const timeoutFormSchema = z.object({
  targetTimeouts: z.array(
    z
      .object({
        deleted: z.number(),
        dst: z.number(),
        lost: z.number(),
      })
      .refine((data) => data.lost < data.deleted, {
        message: 'Lost timeout must not be larger than deleted timeout',
        path: ['lost'], // This will highlight the 'lost' field in case of an error
      })
  ),
});

type TimeoutTargetFormValues = z.infer<typeof timeoutFormSchema>;

interface TimeoutFormItemProps {
  sourceTypeTimeout: SourceTypeTimeouts;
  form: UseFormReturn<TimeoutTargetFormValues>;
  index: number;
}

const TimeoutFormItem = ({
  sourceTypeTimeout,
  form,
  index,
}: TimeoutFormItemProps) => {
  const { messages } = useLocalization();
  let sourceTypeLabel = '';
  let sourceTypeInfoIconText = '';
  if (sourceTypeTimeout.dst === SourceTypes.ais) {
    sourceTypeLabel = messages.settingsTargetLabelAisLabel;
    sourceTypeInfoIconText = messages.settingsTargetAisInfoIconLabel;
  } else if (sourceTypeTimeout.dst === SourceTypes.radar) {
    sourceTypeLabel = messages.settingsTargetLabelRadarLabel;
    sourceTypeInfoIconText = messages.settingsTargetRadarInfoIconLabel;
  } else if (sourceTypeTimeout.dst === SourceTypes.lrit) {
    sourceTypeLabel = messages.settingsTargetLabelLritLabel;
    sourceTypeInfoIconText = messages.settingsTargetLritInfoIconLabel;
  } else if (sourceTypeTimeout.dst === SourceTypes.aisSat) {
    sourceTypeLabel = messages.settingsTargetLabelAisSatLabel;
    sourceTypeInfoIconText = messages.settingsTargetAisSatInfoIconLabel;
  } else if (sourceTypeTimeout.dst === SourceTypes.adsB) {
    sourceTypeLabel = messages.settingsTargetLabelAdsBLabel;
    sourceTypeInfoIconText = messages.settingsTargetAdsBInfoIconLabel;
  } else if (sourceTypeTimeout.dst === SourceTypes.land) {
    sourceTypeLabel = messages.settingsTargetLabelLandLabel;
    sourceTypeInfoIconText = messages.settingsTargetLandInfoIconLabel;
  } else if (sourceTypeTimeout.dst === SourceTypes.aisVessel) {
    sourceTypeLabel = messages.settingsTargetLabelAisVesselLabel;
    sourceTypeInfoIconText = messages.settingsTargetAisVesselInfoIconLabel;
  }
  return (
    <FormItem
      className="flex flex-row border rounded-lg p-2 pr-4 pl-4 pb-4 items-center"
      key={sourceTypeLabel}
    >
      <h3 className="text-lg font-medium">{sourceTypeLabel}</h3>
      <InfoIconTooltip className="mb-4 ml-1">
        {sourceTypeInfoIconText}
      </InfoIconTooltip>
      <div className="flex flex-col flex-grow">
        <div className="flex justify-end space-x-2 items-center">
          <FormDescription>
            {messages.settingsTargetTimeoutLost}
          </FormDescription>
          <FormField
            key={`${SourceTypes[sourceTypeTimeout.dst]}TargetLostKey`}
            control={form.control}
            name={`targetTimeouts.${index}.lost`}
            render={({ field }) => (
              <FormControl>
                <Input
                  type="number"
                  className="w-20"
                  {...field}
                  onChange={(e) => {
                    if (!e.target.value) {
                      field.onChange('');
                    } else {
                      const numberValue = parseInt(e.target.value);
                      field.onChange(numberValue);
                    }
                  }}
                />
              </FormControl>
            )}
          />
          <FormDescription>
            {messages.settingsTargetTimeoutRemoved}
          </FormDescription>
          <FormField
            key={`${SourceTypes[sourceTypeTimeout.dst]}TargetDeletedKey`}
            control={form.control}
            name={`targetTimeouts.${index}.deleted`}
            render={({ field }) => (
              <FormControl>
                <Input
                  type="number"
                  className="w-20"
                  {...field}
                  onChange={(e) => {
                    if (!e.target.value) {
                      field.onChange('');
                    } else {
                      const numberValue = parseInt(e.target.value);
                      field.onChange(numberValue);
                    }
                  }}
                />
              </FormControl>
            )}
          />
        </div>
      </div>
    </FormItem>
  );
};

export const TimeoutForm = () => {
  const { messages } = useLocalization();
  const { toast } = useToast();
  const { settings } = useSettings();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const defaultTimeouts: SourceTypeTimeouts[] = [
    { deleted: 7, dst: SourceTypes.ais, lost: 14 },
    { deleted: 7, dst: SourceTypes.radar, lost: 14 },
    { deleted: 7, dst: SourceTypes.lrit, lost: 14 },
    { deleted: 7, dst: SourceTypes.aisSat, lost: 14 },
    { deleted: 7, dst: SourceTypes.adsB, lost: 14 },
    { deleted: 7, dst: SourceTypes.pps, lost: 14 },
    { deleted: 7, dst: SourceTypes.land, lost: 14 },
    { deleted: 7, dst: SourceTypes.aisVessel, lost: 14 },
  ];

  const { mutate: mutateSettings } = useSaveSettings();

  const initialValues = {
    targetTimeouts:
      // This sorting makes sure AIS is on top, AIS-SAT is second, and the rest are in the same order as the defaultTimeouts
      settings['ribbonPlugin.settings']?.target_timeouts.sort(
        (a: SourceTypeTimeouts, b: SourceTypeTimeouts) => {
          if (a.dst === SourceTypes.ais) return -1;
          if (b.dst === SourceTypes.ais) return 1;
          if (a.dst === SourceTypes.aisSat) return -1;
          if (b.dst === SourceTypes.aisSat) return 1;
          if (a.dst === SourceTypes.aisVessel) return -1;
          if (b.dst === SourceTypes.aisVessel) return 1;
          return 0;
        }
      ) || defaultTimeouts,
  };

  const form = useForm<TimeoutTargetFormValues>({
    resolver: zodResolver(timeoutFormSchema),
    defaultValues: initialValues,
  });

  const { setUnsavedSettings } = useSettingsPage();

  useEffect(() => {
    const subscription = form.watch((formValues) => {
      const hasChanges =
        JSON.stringify(formValues) !== JSON.stringify(initialValues);
      setUnsavedSettings(hasChanges);
    });

    return () => subscription.unsubscribe();
  }, [form, form.watch, initialValues, setUnsavedSettings]);

  function onSubmit(data: TimeoutTargetFormValues) {
    setIsSubmitting(true);
    const settingsToSave = {
      ...settings,
      'ribbonPlugin.settings': {
        ...settings['ribbonPlugin.settings'],
        target_timeouts: data.targetTimeouts,
      },
    };
    mutateSettings(settingsToSave, {
      onSuccess: () => {
        toast({
          className: 'update-track-enable-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.settingsMapTrackSuccessUpdateMessage,
        });
        setIsSubmitting(false);
        setUnsavedSettings(false);
      },
      onError: () => {
        toast({
          className: 'update-track-settings-error-toast',
          title: messages.genericToastTitleError,
          description: messages.settingsMapTrackFailedUpdateMessage,
          variant: 'destructive',
        });
        setIsSubmitting(false);
      },
    });
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <div className="space-y-4">
          <div className="flex items-center space-x-1">
            <div>
              <h3 className="text-lg font-medium">
                {messages.settingsTargetTimeoutLabelTitle}
              </h3>
              <div className="text-sm text-muted-foreground">
                {
                  messages.settingsTargetTimeoutLabelDescription.split(
                    '{lostInfoIcon}'
                  )[0]
                }
                {
                  messages.settingsTargetTimeoutLabelDescription
                    .split('{lostInfoIcon}')[1]
                    .split('{deletedInfoIcon}')[0]
                }
                <InfoIconTooltip>
                  {messages.settingsTargetTimeoutDeletedInfoIcon}
                </InfoIconTooltip>
                {
                  messages.settingsTargetTimeoutLabelDescription.split(
                    '{deletedInfoIcon}'
                  )[1]
                }
              </div>
            </div>
          </div>
          <div className="grid gap-4">
            {form.getValues().targetTimeouts.map((sourceTypeTimeout, index) => {
              return (
                <TimeoutFormItem
                  key={sourceTypeTimeout.dst}
                  sourceTypeTimeout={sourceTypeTimeout}
                  form={form}
                  index={index}
                />
              );
            })}
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 pr-2">
          <Button
            id="settings-target-timeout-save-button"
            onClick={() => {
              if (Object.keys(form.formState.errors).length > 0) {
                toast({
                  className: 'validation-error-toast',
                  title: messages.genericToastTitleError,
                  description: messages.settingsTargetTimeoutValidationError,
                  variant: 'destructive',
                });
              } else {
                form.handleSubmit(onSubmit);
              }
            }}
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? messages.genericSubmitting : messages.genericSubmit}
          </Button>
        </div>
      </form>
    </Form>
  );
};
